import { Action as ReduxAction } from 'redux';
import { DrawingMode, ShapeKind, LineType } from '../types';
import { MultiroleLinkCreateInfo } from '../../../../../common/src/ts/types/multiroleLink';
import { DrawingAttr, PastingImage } from '../reducers/drawingTool';

namespace DrawingToolAction {
  export enum ActionType {
    SET_MODE = 'DRAWING_TOOL_SET_MODE',
    SET_IMAGE_TO_PASTE = 'DRAWING_TOOL_SET_IMAGE_TO_PASTE',
    SET_PASTING_IMAGES = 'DRAWING_TOOL_SET_PASTING_IMAGES',
    SET_MULTIROLE_LINK_TO_PASTE = 'DRAWING_TOOL_SET_MULTIROLE_LINK_TO_PASTE',
    SET_VISIBLE_SHAPE_KINDS = 'DRAWING_TOOL_SET_VISIBLE_SHAPE_KINDS',
    SET_SHOW_HANDWRITING = 'DRAWING_TOOL_SET_SHOW_HANDWRITING',
    SET_FREEHAND_PEN_LINE_WIDTH = 'DRAWING_TOOL_SET_FREEHAND_PEN_LINE_WIDTH',
    SET_ALPHA_ACTION = 'DRAWING_TOOL_SET_ALPHA_ACTION ',
    UPDATE_CURRET_DRAWING_ATTR = 'DRAWING_TOOL_UPDATE_CURRET_DRAWING_ATTR',
    UPDATE_NOTE_DRAWING_ATTR = 'DRAWING_TOOL_UPDATE_NOTE_DRAWING_ATTR',
    SET_DRAWING_ATTR = 'DRAWING_TOOL_SET_DRAWING_ATTR',
    SHOW_OBJECTS = 'DRAWING_TOOL_SHOW_OBJECTS',
    SHOW_LEARNING_PEN_OBJECTS = 'DRAWING_TOOL_SHOW_LEARNING_OBJECTS',
  }

  interface SetModeAction extends ReduxAction {
    type: ActionType.SET_MODE;
    newMode: DrawingMode;
  }

  interface SetImageToPasteAction extends ReduxAction {
    type: ActionType.SET_IMAGE_TO_PASTE;
    newFile: File | null;
  }

  interface SetPastingImagesAction extends ReduxAction {
    type: ActionType.SET_PASTING_IMAGES;
    newPastingImages: ReadonlyArray<PastingImage>;
  }

  interface SetMultiroleLinkToPasteAction extends ReduxAction {
    type: ActionType.SET_MULTIROLE_LINK_TO_PASTE;
    newMultiroleLink: MultiroleLinkCreateInfo;
  }

  interface SetVisibleShapeKindsAction extends ReduxAction {
    type: ActionType.SET_VISIBLE_SHAPE_KINDS;
    newVisibleShapeKinds: ShapeKind[];
  }

  interface SetShowHandwritingAction extends ReduxAction {
    type: ActionType.SET_SHOW_HANDWRITING;
    newShowHandwriting: boolean;
  }

  interface SetAlphaAction extends ReduxAction {
    type: ActionType.SET_ALPHA_ACTION;
    alpha: number;
  }

  interface SetFreehandPenLineWidth extends ReduxAction {
    type: ActionType.SET_FREEHAND_PEN_LINE_WIDTH;
    lineWidth: number;
    handerasingWidth: number;
  }

  interface UpdateCurrentDrawingAttr extends ReduxAction {
    type: ActionType.UPDATE_CURRET_DRAWING_ATTR;
    drawingAttrPartial: Partial<DrawingAttr>;
  }

  interface UpdateNoteDrawingAttr extends ReduxAction {
    type: ActionType.UPDATE_NOTE_DRAWING_ATTR;
    drawingAttrPartial: Partial<DrawingAttr>;
  }

  interface SetDrawingAttrAction extends ReduxAction {
    type: ActionType.SET_DRAWING_ATTR;
    mode: DrawingMode;
    drawingAttrPartial: Partial<DrawingAttr>;
  }

  interface ShowObjectsAction extends ReduxAction {
    type: ActionType.SHOW_OBJECTS;
    showObjects: boolean;
  }

  interface ShowLearningPenObjectsAction extends ReduxAction {
    type: ActionType.SHOW_LEARNING_PEN_OBJECTS;
    showLearningPenObjects: boolean;
  }

  export type Action = SetModeAction | SetImageToPasteAction | SetPastingImagesAction |
    SetMultiroleLinkToPasteAction | SetVisibleShapeKindsAction | SetShowHandwritingAction |
    SetFreehandPenLineWidth | SetAlphaAction | UpdateCurrentDrawingAttr | UpdateNoteDrawingAttr |
    SetDrawingAttrAction | ShowObjectsAction | ShowLearningPenObjectsAction;

  export const setMode = (newMode: DrawingMode): SetModeAction => (
    { newMode, type: ActionType.SET_MODE }
  );

  export const setFillColor = (fillColor: string): UpdateCurrentDrawingAttr => (
    { drawingAttrPartial: { fillColor }, type: ActionType.UPDATE_CURRET_DRAWING_ATTR }
  );

  export const setLineColor = (lineColor: string): UpdateCurrentDrawingAttr => (
    { drawingAttrPartial: { lineColor }, type: ActionType.UPDATE_CURRET_DRAWING_ATTR }
  );

  export const setLineWidth = (lineWidth: number): UpdateCurrentDrawingAttr => (
    { drawingAttrPartial: { lineWidth }, type: ActionType.UPDATE_CURRET_DRAWING_ATTR }
  );

  export const setLineType = (lineType: LineType): UpdateCurrentDrawingAttr => (
    { drawingAttrPartial: { lineType }, type: ActionType.UPDATE_CURRET_DRAWING_ATTR }
  );

  export const setIsTransparent = (isTransparent: boolean): UpdateCurrentDrawingAttr => (
    { drawingAttrPartial: { isTransparent }, type: ActionType.UPDATE_CURRET_DRAWING_ATTR }
  );

  export const setIsFilled = (isFilled: boolean): UpdateCurrentDrawingAttr => (
    { drawingAttrPartial: { isFilled }, type: ActionType.UPDATE_CURRET_DRAWING_ATTR }
  );

  export const updateNoteDrawingtAttr = (
    drawingAttrPartial: Partial<DrawingAttr>,
  ): UpdateNoteDrawingAttr => (
      { drawingAttrPartial, type: ActionType.UPDATE_NOTE_DRAWING_ATTR }
    );

  export const setFreehandPenLineWidth = (
    lineWidth: number,
    handerasingWidth: number,
  ): SetFreehandPenLineWidth => (
      { lineWidth, handerasingWidth, type: ActionType.SET_FREEHAND_PEN_LINE_WIDTH }
    );

  export const setImageToPaste = (newFile: File | null): SetImageToPasteAction => (
    { newFile, type: ActionType.SET_IMAGE_TO_PASTE }
  );

  export const setPastingImages = (
    newPastingImages: ReadonlyArray<PastingImage>,
  ): SetPastingImagesAction => (
      { newPastingImages, type: ActionType.SET_PASTING_IMAGES }
    );

  export const setMultiroleLinkToPaste = (
    newMultiroleLink: MultiroleLinkCreateInfo,
  ): SetMultiroleLinkToPasteAction => (
      { newMultiroleLink, type: ActionType.SET_MULTIROLE_LINK_TO_PASTE }
    );

  export const setVisibleShapeKinds =
    (newVisibleShapeKinds: ShapeKind[]): SetVisibleShapeKindsAction => (
      { newVisibleShapeKinds, type: ActionType.SET_VISIBLE_SHAPE_KINDS }
    );

  export const setAlpha = (alpha: number): SetAlphaAction => (
    { alpha, type: ActionType.SET_ALPHA_ACTION }
  );

  export const setShowHandwriting = (newShowHandwriting: boolean): SetShowHandwritingAction => (
    { newShowHandwriting, type: ActionType.SET_SHOW_HANDWRITING }
  );

  export const setDrawingAttr = (
    mode: DrawingMode,
    drawingAttrPartial: Partial<DrawingAttr>,
  ): SetDrawingAttrAction => (
    { mode, drawingAttrPartial, type: ActionType.SET_DRAWING_ATTR }
  );

  export const showObjects = (showObjects: boolean): ShowObjectsAction => (
    { showObjects, type: ActionType.SHOW_OBJECTS }
  );

  export const showLearningPenObjects = (
    showLearningPenObjects: boolean,
  ): ShowLearningPenObjectsAction => (
    { showLearningPenObjects, type: ActionType.SHOW_LEARNING_PEN_OBJECTS }
  );

  export function isDomesticAction(action: ReduxAction): action is Action {
    return Object.keys(ActionType).some(k => action.type === ActionType[k as any]);
  }
}

export default DrawingToolAction;
